import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { GroupListItemDataFragment } from '../GroupListing/GroupList.api';
import { useFetchCoursesQuery } from './Courses.api';
import type { GroupFormData } from './GroupFormConfig';
import { defaultGroupFormValues, getFieldsToValidate, groupFormSchema } from './GroupFormConfig';
import type { FormMode } from './useGroupFormState';
import { useGroupFormState } from './useGroupFormState';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import InputField from '@/components/fields/InputField';
import MultiSelectField from '@/components/fields/MultiSelectField';
import TextareaField from '@/components/fields/TextareaField';

interface GroupFormProps {
    mode: FormMode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    group?: GroupListItemDataFragment | null;
    companyId?: string;
    onSubmit: (data: GroupFormData) => Promise<void>;
    onClose: () => void;
}

const GroupFormContainer = ({ mode, open, title, onSubmit, group, onClose, companyId }: GroupFormProps) => {
    const { t } = useTranslation('groupDetails');
    const { t: tGlobal } = useTranslation('common');

    const initialData = group
        ? {
              name: group?.name || '',
              description: group?.description || '',
              enrolledCourseIds: group?.enrolledCourseIds || [],
          }
        : undefined;

    const { data } = useFetchCoursesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: companyId || '',
        },
        skip: !open || !companyId,
    });

    const coursesOptions = useMemo(() => {
        const coursesOptions = (data?.courseInstances?.items || []).map(course => ({
            value: course.id,
            label: course.name,
        }));

        return coursesOptions;
    }, [data?.courseInstances.items]);

    // Initialize form state
    const { state, actions } = useGroupFormState(mode, initialData);
    const form = useForm<GroupFormData>({
        mode: 'onChange',
        resolver: zodResolver(groupFormSchema),
        defaultValues: state.formData,
    });

    useEffect(() => {
        if (companyId && open) {
            const formData: GroupFormData = {
                name: initialData?.name || '',
                description: initialData?.description || '',
                enrolledCourseIds: initialData?.enrolledCourseIds || [],
            };
            actions.initialize(formData);
            form.reset(formData);
        }
    }, [companyId, open, form, actions, initialData?.name, initialData?.description, initialData?.enrolledCourseIds]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                actions.reset();
                form.reset(defaultGroupFormValues);
            }, 150);
        }
    }, [open, actions, form]);

    const handleSubmit = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }
        try {
            actions.submitStart();
            await onSubmit(form.getValues() as GroupFormData);
            actions.submitSuccess();
            onClose();
        } catch (error) {
            actions.submitError();
            if (error && typeof error === 'object' && 'fields' in error) {
                const fields = error.fields as { field: string; message: string }[];
                console.error('Validation errors:', fields);
                fields.forEach(err => {
                    if (err.field && err.message) {
                        form.setError(err.field as keyof GroupFormData, {
                            type: 'manual',
                            message: err.message,
                        });
                    }
                });
            }
        }
    }, [form, onSubmit, actions, onClose]);

    const modalFooter = (
        <div className="flex items-center justify-end gap-2.5">
            <Button outline onClick={onClose} disabled={state.isSubmitting} compact>
                {tGlobal('modal.cancel')}
            </Button>
            <Button color="teal" onClick={handleSubmit} disabled={state.isSubmitting} compact>
                {mode === 'edit' ? t('modalEditGroup.updateButton') : t('modalCreateGroup.createButton')}
            </Button>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={title}
                subtitle={mode === 'create' ? t('modalCreateGroup.subtitle') : undefined}
                largeTitle
                footer={modalFooter}
                className="!w-[512px] !max-w-[512px] !p-5"
                showCloseButton={false}
            >
                {/* Form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <InputField
                        label={t('fields.name.label')}
                        name="name"
                        placeholder={t('fields.name.placeholder')}
                        required
                        disabled={false}
                        labelSize="base"
                        labelWeight="semibold"
                    />
                    <TextareaField
                        label={t('fields.description.label')}
                        name="description"
                        placeholder={t('fields.description.placeholder')}
                        rows={4}
                        labelSize="base"
                        labelWeight="semibold"
                    />
                    {coursesOptions.length > 0 ? (
                        <div className="flex flex-col items-start justify-start gap-1.5">
                            {/* Label */}
                            <div className="flex items-start justify-start gap-1.5">
                                <div className="justify-start">
                                    <span className="pr-1 leading-6 font-semibold text-neutral-800">
                                        {t('fields.courses.label')}
                                    </span>
                                    <span className="leading-6 font-semibold text-red-500">*</span>
                                </div>
                            </div>

                            {/* Description text */}
                            <div className="leading-6 font-medium text-neutral-500">
                                {t('fields.courses.description')}
                            </div>

                            {/* Input field without label */}
                            <MultiSelectField
                                name="enrolledCourseIds"
                                options={coursesOptions}
                                placeholder={t('fields.courses.placeholder')}
                                searchable
                                required
                            />
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500">{t('fields.courses.empty')}</div>
                    )}
                </form>
            </Modal>
        </FormProvider>
    );
};

export default GroupFormContainer;
